import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const IngestionTasks = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">Ingestion Tasks</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Monitor and track document ingestion tasks. When documents are uploaded, ingestion tasks are created
        to process them for semantic search and knowledge graph extraction. Poll these endpoints to check task status.
      </p>

      <EndpointCard
        method="GET"
        path="/ingestionTasks/{task_uuid}"
        title="Get Ingestion Task"
        description="Retrieves the status of a document ingestion task. Use this to poll for completion status after uploading a document."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            task_uuid: { required: true, description: 'The unique identifier of the ingestion task' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>task_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the ingestion task</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/ingestionTasks/{task_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "document_uuid": "{document_uuid}",
  "status": "COMPLETED",
  "message": "Document processed successfully.",
  "chunks_created": 15,
  "embeddings_generated": true,
  "knowledge_graph_updated": true,
  "created_at": "2024-01-15T10:00:00Z",
  "completed_at": "2024-01-15T10:01:30Z"
}`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Task Status Values:</h4>
            <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside ml-2">
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">PENDING</code> - Task is queued for processing</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">PROCESSING</code> - Document is being chunked, embedded, and processed</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">COMPLETED</code> - Document is ready for use in search and SOP generation</li>
              <li><code className="bg-gray-800 px-1 py-0.5 rounded">FAILED</code> - Processing failed (check message field for details)</li>
            </ul>
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default IngestionTasks;
